import PropTypes from 'prop-types';
import { useMemo, useContext } from 'react';

import { Box, Drawer } from '@material-ui/core';
// icons
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
// material
import { experimentalStyled as styled } from '@material-ui/core/styles';

import LazySize from '../../components/LazySize';
// components
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden, MIconButton } from '../../components/@material-extend';
// configs

import useAuth from '../../hooks/useAuth';
import sidebarConfig from './SidebarConfig';
// hooks
import useLocales from '../../hooks/useLocales';
// context
import { OpenIdContext } from '../../contexts/OpenIdContext';
import pagePermissionConfig from '../../routes/pagePermissionConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: open && DRAWER_WIDTH
  },
  width: 0,
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.leavingScreen
  })
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { role } = useContext(OpenIdContext);
  const { translate, currentLang } = useLocales();
  const { logout } = useAuth() || {};

  let roleActivity = [];
  try {
    roleActivity = JSON.parse(localStorage.getItem('roleActivity'));
  } catch (error) {
    logout();
  }

  const sidebarAuthorizedConfig = useMemo(() => {
    if (role) {
      let pagesAuthorize = pagePermissionConfig
        .filter((pagePerm) => role && role.includes(pagePerm.role))
        .reduce((prev, cur) => [...prev, ...cur.pages.map((o) => o.title)], [])
        .filter((page, idx, self) => idx === self.indexOf(page));

      if (roleActivity) pagesAuthorize = roleActivity.map((o) => o.PageName);

      return sidebarConfig.map((config) => ({
        items: config.items
          .filter((item) => pagesAuthorize.some((o) => o === item.title))
          .map((item) => ({
            ...item,
            key: item.title,
            title: item.translateKey ? translate(item.translateKey) : item.title,
            children: item.children
              .filter((child) => pagesAuthorize.some((o) => o === child.title))
              .map((child) => ({
                ...child,
                key: child.title,
                title: child.translateKey ? translate(child.translateKey) : child.title
              }))
          }))
      }));
    }

    return [];
  }, [role, roleActivity, translate]);

  const renderContent = (
    <Scrollbar
      sx={{ height: '100%', '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ px: 2.5, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <LazySize alt="logo_scg" sx={{ width: '100%' }} src={`${process.env.PUBLIC_URL}/static/brand/logo_scg.png`} />
        </Box>
        <MIconButton onClick={onCloseSidebar}>
          <ArrowBackIosIcon />
        </MIconButton>
      </Box>

      <NavSection navConfig={sidebarAuthorizedConfig} />
    </Scrollbar>
  );

  return (
    <RootStyle open={isOpenSidebar}>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open={isOpenSidebar}
          variant="persistent"
          PaperProps={{
            sx: { width: DRAWER_WIDTH, bgcolor: 'background.default' }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
